<template>
	<teleport to="body">
		<transition name="toggle-fade" appear>
			<div v-if="open" class="modal-bg"></div>
		</transition>
		<transition name="toggle-scale" appear>
			<div v-if="open" class="prompt-contain update-prompt" role="dialog" aria-modal="true" aria-labelledby="updatePromptTitle">
				<div class="prompt-head">
					<div class="icon-contain">
						<img src="@/images/app-icon.png" />
					</div>
					<div class="content-contain">
						<strong id="updatePromptTitle" class="action">{{ $t('prompts.updateAvailable') }}</strong>
						<span class="desc">{{ $t('prompts.updateReady') }}</span>
					</div>
				</div>
				<div class="prompt-body">
					<div class="answer-buttons">
						<button @click="refresh()" class="btn-confirm" :disabled="refreshing">
							<svg class="icon"><use href="#download"></use></svg> {{ $t('prompts.updateNow') }}
							<span v-if="refreshing" class="is-loading dots">
								<span class="dot-1"></span>
								<span class="dot-2"></span>
								<span class="dot-3"></span>
							</span>
						</button>
					</div>
				</div>
			</div>
		</transition>
	</teleport>
</template>

<script setup>
import { useStore } from '@/store';
import { computed, ref } from 'vue';

const store = useStore();
const open = computed(() => store.serviceWorkerUpdate);
const refreshing = ref(false);

let reloadTriggered = false;
let updateTimeout;
let controllerChangeTimeout;

async function refresh() {
	if (refreshing.value) return; // Prevent multiple clicks

	refreshing.value = true;
	store.serviceWorkerUpdate = false;
	const message = { type: 'skipWaiting' };

	try {
		// IMPORTANT: send skipWaiting to the *waiting* worker (if present).
		// Messaging the active controller will not activate a waiting update.
		const registration = await navigator.serviceWorker?.getRegistration?.();
		if (registration?.waiting) {
			registration.waiting.postMessage(message);
		} else if (registration?.active) {
			// Best-effort fallback (e.g. if the SW script is coded to handle it)
			registration.active.postMessage(message);
		} else if ('BroadcastChannel' in window) {
			// Legacy fallback
			const broadcast = new BroadcastChannel('refresh');
			broadcast.postMessage(message);
		} else if (navigator.serviceWorker?.controller) {
			navigator.serviceWorker.controller.postMessage(message);
		}

		// Timeout fallback in case skipWaiting doesn't work
		clearTimeout(updateTimeout);
		updateTimeout = setTimeout(() => {
			if (!reloadTriggered) {
				console.warn('Service worker update timeout, forcing reload');
				reloadTriggered = true;
				window.location.reload();
			}
		}, 5000);
	} catch (error) {
		console.error('Error triggering service worker update:', error);
		// Fallback: direct reload on error
		if (!reloadTriggered) {
			reloadTriggered = true;
			setTimeout(() => window.location.reload(), 1000);
		}
	}
}
navigator.serviceWorker.addEventListener('controllerchange', () => {
	if (reloadTriggered) return;
	if (controllerChangeTimeout) {
		clearTimeout(controllerChangeTimeout);
	}

	// Debounce the reload to prevent multiple rapid reloads
	controllerChangeTimeout = setTimeout(() => {
		if (reloadTriggered) return;
		reloadTriggered = true;
		refreshing.value = true;
		console.log('Service worker controller changed, reloading page');
		window.location.reload();
	}, 100);
});
</script>
